import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  Typography,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  VolumeUp as VolumeUpIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppData } from '../../context/AppDataContext';
import { useStaffRealtime } from '../../context/StaffRealtimeContext';
import { formatUtcDateTime } from '../../utils/dateFormatUtils';
import {
  getStaffNotificationPath,
  getStaffNotificationBody,
  getStaffNotificationTitle,
  getWaitlistAlertFromNotification,
  getAppointmentConfirmationAlertFromNotification,
} from '../../utils/staffNotificationNavigation';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import PushNotificationControl from '../notifications/PushNotificationControl';
import { openPushPrompt } from '../../utils/pushPrompt';

const iconVariantSx = {
  default: {
    color: BRAND.text,
    bgcolor: brandAlpha('accent', 0.1),
    '&:hover': { bgcolor: brandAlpha('accent', 0.2) },
  },
  plain: {
    color: BRAND.textMuted,
    '&:hover': { color: BRAND.accentText, bgcolor: brandAlpha('accent', 0.06) },
  },
};

const StaffNotificationBell = ({ variant = 'default' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentStore } = useAppData();
  const {
    notifications,
    unreadCount,
    soundsEnabled,
    enableSounds,
    markRead,
    markAllRead,
    openWaitlistAlert,
    openConfirmationAlert,
  } = useStaffRealtime();
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEnableSounds = async () => {
    await enableSounds();
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllRead();
    } catch (error) {
      console.error('Failed to mark notifications as read', error);
    }
  };

  const handleNotificationClick = async (notification) => {
    handleClose();
    const target = getStaffNotificationPath(notification);
    if (target) {
      navigate(target);
    }
    const waitlistAlert = getWaitlistAlertFromNotification(notification);
    if (waitlistAlert) {
      openWaitlistAlert?.(waitlistAlert);
    }
    const confirmationAlert = getAppointmentConfirmationAlertFromNotification(notification);
    if (confirmationAlert) {
      openConfirmationAlert?.(confirmationAlert);
    }
    if (!notification?.isRead) {
      try {
        await markRead(notification.id);
      } catch (error) {
        console.error('Failed to mark notification as read', error);
      }
    }
  };

  const handleSeeAll = () => {
    handleClose();
    if (!currentStore?.id) {
      return;
    }
    navigate(`/management/stores/${currentStore.id}/notifications`);
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={t('staffNotifications.title')}
        sx={iconVariantSx[variant] ?? iconVariantSx.default}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        disableScrollLock
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: 'min(100vw - 24px, 360px)', sm: 380 },
            maxHeight: 480,
            borderRadius: BRAND_RADIUS.button,
            border: `1px solid ${BRAND.line}`,
            boxShadow: `0 12px 32px ${brandAlpha('primary', 0.12)}`,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {t('staffNotifications.title')}
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead} sx={{ color: BRAND.accentText, textTransform: 'none' }}>
              {t('staffNotifications.markAllRead')}
            </Button>
          )}
        </Box>

        <Box
          sx={{ px: 2, pb: 1 }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <PushNotificationControl
            audience="staff"
            compact
            onEnableRequest={() => {
              handleClose();
              openPushPrompt('staff');
            }}
          />
        </Box>

        {!soundsEnabled && (
          <Box sx={{ px: 2, pb: 1.5 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<VolumeUpIcon />}
              onClick={handleEnableSounds}
              sx={{
                borderColor: BRAND.accent,
                color: BRAND.accentText,
                textTransform: 'none',
                '&:hover': { borderColor: BRAND.accentDark, bgcolor: brandAlpha('accent', 0.08) },
              }}
            >
              {t('staffNotifications.enableSounds')}
            </Button>
          </Box>
        )}

        <Divider />

        {notifications.length === 0 ? (
          <Box sx={{ px: 2, py: 3 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {t('staffNotifications.empty')}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ overflowY: 'auto', maxHeight: 320 }}>
            {notifications.map((notification) => (
              <ListItemButton
                key={notification.id}
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  alignItems: 'flex-start',
                  bgcolor: notification.isRead ? 'transparent' : brandAlpha('accent', 0.08),
                }}
              >
                <ListItemText
                  primary={getStaffNotificationTitle(notification, t)}
                  secondary={(
                    <>
                      <Typography component="span" variant="body2" color="text.secondary" display="block">
                        {getStaffNotificationBody(notification, t)}
                      </Typography>
                      <Typography component="span" variant="caption" color="text.disabled">
                        {formatUtcDateTime(notification.createdAtUtc)}
                      </Typography>
                    </>
                  )}
                  primaryTypographyProps={{
                    variant: 'body2',
                    fontWeight: notification.isRead ? 500 : 700,
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        )}

        <Divider />
        <Box sx={{ px: 1, py: 0.5 }}>
          <Button
            fullWidth
            onClick={handleSeeAll}
            disabled={!currentStore?.id}
            sx={{ color: BRAND.accentText, textTransform: 'none', fontWeight: 700 }}
          >
            {t('staffNotifications.seeAll')}
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default StaffNotificationBell;
